import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaPlus, FaTimes, FaTrash, FaEdit, FaFileInvoiceDollar, FaChevronDown, FaChevronUp, FaSearch, FaExclamationTriangle } from 'react-icons/fa';
import { FiCheckCircle } from 'react-icons/fi';
import instanciaAxios, { esErrorPermiso } from '../../miscelaneo/instanciaAxios';
import InputProductoFacturacion from '../util/Inputs/InputProductoFacturacion';
import InputClientes from '../util/Inputs/InputClientes';
import TablaEntidad from '../util/tablas/TablaEntidad';
import PlantillaModal from '../util/PlantillaModal';
import DockVerticalDerecha from '../estructura/DockVerticalDerecha';
import FuncionesGenerales from '../util/FuncionesGenerales';
import { useContextoSesion } from '../../miscelaneo/ContextoSesion';
import SpinnerCargando from '../util/SpinnerCargando';
import ModalCrearClienteRapido from './ModalCrearClienteRapido';
import ModalCrearProductoRapido from './ModalCrearProductoRapido';
import ModalPagoPOS from './ModalPagoPOS';
import ModalPDFBase64 from '../util/ModalPDFBase64';
import ModalConfirmacion from '../util/ModalConfirmacion';
import { construirPayloadFactura, procesarRespuestaFactura, obtenerMensajeExito, mapearCotizacionATicket, calcularFechaMinimaEmisionISO } from '../../miscelaneo/facturacionHelpers';
import useTicketManager from '../../hooks/useTicketManager';
import { roundTo2 } from '../../miscelaneo/monetario';
import { noEsFechaHoraFutura } from '../util/ValidacionesCampos';
import { useImpresion } from '../../contexts/ContextoImpresion';
import { useBalanza } from '../../contexts/ContextoBalanza';
import { useScanner } from '../../contexts/ContextoScanner';

function resolverNombreClienteFactura(factura = {}) {
  const razonSocial = String(factura.cliente_razon_social || factura.razon_social || '').trim();
  if (razonSocial) return razonSocial;

  const nombreCompleto = String(
    factura.cliente_nombre ||
    factura.nombre_cliente ||
    [factura.nombre_comprador, factura.apellido_comprador].filter(Boolean).join(' ')
  ).trim();

  return nombreCompleto || 'CONSUMIDOR FINAL';
}

export default function ProcesoFacturaDemo() {
  const location = useLocation();
  const { usuario, sesionCaja, sesionCajaCargada, cargando: cargandoContexto, tienePermiso } = useContextoSesion();
  const { formatoMoneda, obtenerFechaHoraActual, formatearFechaHoraInput, obtenerFechaActual } = FuncionesGenerales(usuario);

  // Hook compartido — Factura Demo NO usa comprobante fiscal (null)
  const tm = useTicketManager({ defaultTipoComprobante: null });
  const { imprimirDocumento } = useImpresion();
  const { pesoActual, unidadPeso, balanzaEstable, balanzaConectada, pesoBloqueado, bloquearPeso, desbloquearPeso, sinDatos } = useBalanza();
  const { scannerConectado, habilitado: scannerHidHabilitado, registrarListener } = useScanner();

  // Estado para modal de creación rápida de producto
  const [mostrarModalNuevoProducto, setMostrarModalNuevoProducto] = useState(false);

  // Estado para resumen de inventario post-edición
  const [resumenInventario, setResumenInventario] = useState(null);
  const [mostrarResumenInventario, setMostrarResumenInventario] = useState(false);
  const [pagosAnulados, setPagosAnulados] = useState(0);
  const [confirmacionPagosAnulados, setConfirmacionPagosAnulados] = useState(false);

  // Estado para búsqueda de facturas editables
  const [mostrarModalBuscarEditable, setMostrarModalBuscarEditable] = useState(false);
  const [facturasEditables, setFacturasEditables] = useState([]);
  const [cargandoEditables, setCargandoEditables] = useState(false);
  const [filtroEditable, setFiltroEditable] = useState('');

  // Focus automático en input de producto cuando todo está listo
  useEffect(() => {
    if (
      !tm.cargando &&
      !cargandoContexto &&
      sesionCajaCargada &&
      sesionCaja &&
      !sesionCaja.es_sesion_vieja &&
      tm.sucursalSeleccionada &&
      tm.inputProductoRef.current
    ) {
      setTimeout(() => {
        tm.inputProductoRef.current?.focus?.();
      }, 200);
    }
  }, [tm.cargando, cargandoContexto, sesionCajaCargada, sesionCaja, tm.sucursalSeleccionada]);

  // === EDIT MODE: Cargar factura existente para editar ===
  const buscarFacturasEditables = async (busqueda) => {
    try {
      setCargandoEditables(true);
      const params = { limite: 50, sin_ncf: true };
      if (busqueda) params.busqueda = busqueda;

      const response = await instanciaAxios.get('/factura', { params });
      const facturas = response.data?.facturas || response.data || [];
      // Filtrar solo las que NO tienen NCF (editables)
      const editables = facturas.filter(f => !f.ncf);
      setFacturasEditables(editables);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al buscar facturas editables:', error);
      toast.error('Error al buscar facturas');
    } finally {
      setCargandoEditables(false);
    }
  };

  const cargarFacturaParaEditar = async (factura) => {
    try {
      // Obtener detalles completos de la factura
      const response = await instanciaAxios.get(`/factura/${factura.id_factura}`);
      const facturaCompleta = response.data;

      // Mapear detalles a formato de producto del ticket
      const productos = (facturaCompleta.detalles_factura || []).map(det => ({
        id_producto: det.id_producto,
        descripcion: det.descripcion_producto || det.descripcion,
        precio: parseFloat(det.precio) || 0,
        precioOriginal: parseFloat(det.precio) || 0,
        precioBase: parseFloat(det.precio) || 0,
        precioLista: null,
        fuentePrecio: 'base',
        precioEditado: false,
        cantidad: parseFloat(det.cantidad) || 1,
        indicador_facturacion: det.indicador_facturacion || 1,
        itbis_tasa: parseFloat(det.itbis_tasa) || 0,
        id_tipo_producto: det.indicador_bien_servicio || det.id_tipo_producto || 1
      }));

      // Guardar productos originales para calcular diff
      const productosOriginales = productos.map(p => ({ ...p }));

      // Cargar en el ticket activo
      const nuevosTickets = [...tm.tickets];
      const ticket = nuevosTickets[tm.ticketActivo];
      
      ticket.productos = productos;
      ticket.clienteSeleccionado = facturaCompleta.id_entidad_cliente || null;
      ticket.id_entidad_cliente_original = facturaCompleta.id_entidad_cliente || null;
      ticket.id_lista_precio = facturaCompleta.id_lista_precio || null;
      ticket.nombreCliente = resolverNombreClienteFactura(facturaCompleta);
      ticket.comentario = facturaCompleta.comentario || '';
      ticket.fecha_hora_creacion = formatearFechaHoraInput(
        facturaCompleta.fecha_hora || facturaCompleta.fecha_hora_creacion || obtenerFechaHoraActual()
      );
      ticket.id_factura_editando = factura.id_factura;
      ticket.productosOriginales = productosOriginales;
      ticket.tipoComprobanteFiscal = null; // Sin comprobante

      // Recalcular totales
      tm.calcularTotales(ticket);
      tm.setTickets(nuevosTickets);

      setMostrarModalBuscarEditable(false);
      toast.success(`Factura #${factura.id_factura} cargada para edición`);
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al cargar factura:', error);
      toast.error('Error al cargar la factura para edición');
    }
  };

  // tm.cargando arranca en `false` y solo pasa a `true` una vez que el efecto
  // interno de useTicketManager se dispara (async). Hay que esperar a que haya
  // pasado por `true` al menos una vez para no precargar antes de que ese efecto
  // termine de aplicar el cliente por defecto del tenant (que si no, sobrescribe
  // el cliente de la cotización después de nuestra precarga).
  const huboCargandoRef = useRef(false);
  useEffect(() => {
    if (tm.cargando) huboCargandoRef.current = true;
  }, [tm.cargando]);

  // Precargar el ticket cuando se llega desde la conversión de una cotización
  // (PantallaCotizaciones / ProcesoCotizacion navegan con state.cotizacionOrigen).
  // No crea nada — la factura solo se crea cuando el usuario presiona Cobrar.
  const cotizacionOrigenProcesadaRef = useRef(false);
  useEffect(() => {
    const cotizacionOrigen = location.state?.cotizacionOrigen;
    if (!cotizacionOrigen || cotizacionOrigenProcesadaRef.current) return;
    if (!huboCargandoRef.current || tm.cargando || cargandoContexto) return;
    cotizacionOrigenProcesadaRef.current = true;
    // Limpiar el state para evitar re-carga en hot-reload o navegación posterior
    window.history.replaceState({}, document.title);

    const nuevosTickets = [...tm.tickets];
    const ticket = nuevosTickets[tm.ticketActivo];
    Object.assign(ticket, mapearCotizacionATicket(cotizacionOrigen, { formatearFechaHoraInput, obtenerFechaHoraActual }));
    ticket.tipoComprobanteFiscal = null;

    tm.calcularTotales(ticket);
    tm.setTickets(nuevosTickets);
    toast.success(`Cotización #${cotizacionOrigen.id_cotizacion} cargada — revise y presione Cobrar para crear la factura`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.cotizacionOrigen, tm.cargando, cargandoContexto]);

  // Atajo de teclado: End para abrir modal de cobrar
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.key === 'End' &&
        !tm.mostrarModalPago &&
        !tm.mostrarModalCantidad &&
        !tm.procesandoCobro &&
        !mostrarResumenInventario &&
        !mostrarModalBuscarEditable &&
        !tm.confirmacionCambioLista.mostrar
      ) {
        e.preventDefault();
        cobrarTicket();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [tm.mostrarModalPago, tm.mostrarModalCantidad, tm.procesandoCobro, mostrarResumenInventario, mostrarModalBuscarEditable, tm.confirmacionCambioLista.mostrar, tm.ticketActual, tm.sucursalSeleccionada]);

  // Scanner QZ HID: recibir barcodes del contexto y pasarlos al input de producto
  useEffect(() => {
    if (!scannerHidHabilitado || !scannerConectado) return;
    const unsubscribe = registrarListener((barcode) => {
      tm.inputProductoRef.current?.obtenerProducto(barcode, { rapido: true });
    });
    return unsubscribe;
  }, [scannerHidHabilitado, scannerConectado, registrarListener, tm.inputProductoRef]);

  // === COBRAR / GUARDAR ===
  const cobrarTicket = () => {
    const ticket = tm.ticketActual;
    
    if (ticket.productos.length === 0) {
      toast.error('El ticket está vacío');
      return;
    }
    
    if (!tm.sucursalSeleccionada) {
      toast.error('Debe seleccionar una sucursal');
      return;
    }

    // In edit mode, skip payment modal — update directly.
    // Payments are handled separately via CxC after the edit.
    if (ticket.id_factura_editando) {
      procesarPago(null);
      return;
    }

    if (!ticket.fecha_emision) {
      toast.error('Debe indicar la fecha de emisión');
      return;
    }
    if (ticket.fecha_emision > obtenerFechaActual()) {
      toast.error('La fecha de emisión no puede ser futura');
      return;
    }
    if (ticket.fecha_emision < calcularFechaMinimaEmisionISO()) {
      toast.error('La fecha de emisión no puede ser anterior a 1.5 meses atrás');
      return;
    }

    tm.setMostrarModalPago(true);
  };

  const actualizarComentarioTicket = (comentario) => {
    const nuevosTickets = [...tm.tickets];
    nuevosTickets[tm.ticketActivo].comentario = comentario;
    tm.setTickets(nuevosTickets);
  };

  const normalizarComentarioTicket = () => {
    const comentarioActual = tm.tickets[tm.ticketActivo]?.comentario;
    if (typeof comentarioActual !== 'string') return;
    const comentarioNormalizado = comentarioActual.trim();
    if (comentarioNormalizado === comentarioActual) return;
    actualizarComentarioTicket(comentarioNormalizado);
  };

  const procesarPago = async (datosPago) => {
    const ticket = tm.ticketActual;
    
    if (tm.procesandoCobro) return;
    
    try {
      tm.setProcesandoCobro(true);

      const fechaHoraCreacion = ticket.fecha_hora_creacion || obtenerFechaHoraActual();
      if (!noEsFechaHoraFutura(fechaHoraCreacion)) {
        toast.error('La fecha y hora de creación no puede ser futura');
        return;
      }

      if (ticket.id_factura_editando) {
        // === MODO EDICIÓN: Actualizar factura existente ===
        await actualizarFacturaExistente(ticket, datosPago);
      } else {
        // === MODO CREACIÓN: Nueva factura sin comprobante ===
        await crearFacturaNueva(ticket, datosPago);
      }
      
    } catch (error) {
      if (esErrorPermiso(error)) return;
      console.error('Error al procesar:', error);
      
      if (error.response?.status === 400) {
        const mensaje = error.response?.data?.mensaje || error.response?.data?.message || '';
        const errorCode = error.response?.data?.error_code || '';
        
        if (errorCode === 'STOCK_INSUFICIENTE') {
          toast.error(`Stock insuficiente:\n\n${mensaje}`, { autoClose: 10000 });
          return;
        }
        
        if (errorCode === 'FACTURA_FISCAL_INMUTABLE') {
          toast.error('Esta factura tiene comprobante fiscal y no puede ser editada.');
          return;
        }
        
        toast.error(mensaje || 'Error de validación. Revise los datos ingresados');
        return;
      }
      
      const mensajeError = error.response?.data?.message || error.response?.data?.error || 'Error al procesar';
      toast.error(mensajeError);
    } finally {
      tm.setProcesandoCobro(false);
    }
  };

  const crearFacturaNueva = async (ticket, datosPago) => {
    const payload = construirPayloadFactura({
      ticket: { ...ticket, tipoComprobanteFiscal: null },
      usuario,
      sucursalSeleccionada: tm.sucursalSeleccionada,
      datosPago,
      metodosPago: tm.metodosPago,
      obtenerFechaHoraActual
    });

    // Override: sin comprobante fiscal
    payload.tipo_comprobante = null;
    payload.id_cotizacion_origen = ticket.id_cotizacion_origen || null;

    console.log('📦 Payload factura demo (nueva):', payload);
    
    const respuesta = await instanciaAxios.post('/factura/', payload);
    
    const resultado = procesarRespuestaFactura(respuesta);
    toast.success('Factura creada exitosamente (sin comprobante fiscal)');

    if (resultado.cambio > 0) {
      toast.info(`Cambio a devolver: ${formatoMoneda(resultado.cambio)}`, { autoClose: 8000 });
    }
    
    tm.setFacturaCreada(resultado);

    if (resultado.pdf_base64) {
      tm.setPdfBase64(resultado.pdf_base64);
    }

    tm.limpiarTicketActivo();
    tm.setMostrarModalPago(false);

    // Imprimir via QZ Tray (ESC/POS) si está disponible, sino fallback a browser
    await imprimirDocumento({
      idDocumento: resultado.id_factura,
      tipoDocumento: 'factura',
      pdfBase64: resultado.pdf_base64
    });
  };

  const actualizarFacturaExistente = async (ticket, datosPago) => {
    // Construir detalles
    const detallesFactura = ticket.productos.map(p => ({
      id_producto: p.id_producto,
      descripcion_producto: p.descripcion,
      cantidad: p.cantidad,
      precio: p.precio,
      descuento: 0,
      indicador_facturacion: p.indicador_facturacion,
      itbis_tasa: p.itbis_tasa,
      indicador_bien_servicio: p.id_tipo_producto || 1,
      fuente_precio: p.fuentePrecio || 'base'
    }));

    const payload = {
      id_entidad_cliente: ticket.clienteSeleccionado ?? ticket.id_entidad_cliente_original ?? null,
      id_sucursal: tm.sucursalSeleccionada,
      id_lista_precio: ticket.id_lista_precio ?? null,
      fecha_hora_creacion: ticket.fecha_hora_creacion || obtenerFechaHoraActual(),
      comentario: typeof ticket.comentario === 'string' ? ticket.comentario.trim() || null : null,
      detallesFactura,
      monto: roundTo2(ticket.totales.subtotal),
      impuesto_total: roundTo2(ticket.totales.itbis),
      monto_total: roundTo2(ticket.totales.total)
    };

    console.log('📦 Payload factura demo (actualización):', payload);
    
    const respuesta = await instanciaAxios.put(
      `/factura/${ticket.id_factura_editando}/completa`,
      payload
    );

    const data = respuesta.data?.data || respuesta.data;

    console.log('[EDIT RESPONSE factura]', { pagos_anulados: data.pagos_anulados, resumen_inventario: data.resumen_inventario });
    
    // Store PDF for later display
    if (data.pdf?.pdf_base64) {
      tm.setPdfBase64(data.pdf.pdf_base64);
    }
    
    tm.setFacturaCreada({
      id_factura: ticket.id_factura_editando,
      monto_total: data.monto_total,
      pdf_base64: data.pdf?.pdf_base64 || null
    });
    
    // ALWAYS show modal after edit — sections render conditionally inside
    setResumenInventario(data.resumen_inventario || []);
    setPagosAnulados(data.pagos_anulados || 0);
    setConfirmacionPagosAnulados(data.pagos_anulados > 0 ? false : true);
    setMostrarResumenInventario(true);
    // Don't show PDF or clean ticket yet - wait for user to click "Entendido"
    return;
  };

  // Dock handlers
  const handleBotonEditar = () => {
    setMostrarModalBuscarEditable(true);
    buscarFacturasEditables('');
  };

  if (tm.cargando || cargandoContexto || !sesionCajaCargada) {
    return <SpinnerCargando />;
  }

  // Verificar si tiene caja abierta (solo después de que sesionCajaCargada sea true)
  if (!sesionCaja) {
    return (
      <div className="min-h-screen bg-gray-200 font-sans">
        <div className="flex justify-center py-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 max-w-md text-center">
            <div className="bg-yellow-100 rounded-full p-5 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <FaExclamationTriangle className="text-yellow-500 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">Caja no abierta</h2>
            <p className="text-gray-600 mb-6">
              Debes abrir una caja antes de poder facturar. Esto permite rastrear todos los movimientos de tu turno.
            </p>
            <Link
              to="/AperturaCaja"
              state={{ from: location.pathname }}
              className="inline-block px-6 py-3 bg-green-600 text-white rounded-xl font-semibold hover:bg-green-700 transition-all duration-200 shadow-md"
            >
              Abrir Caja
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Verificar si es sesión vieja
  if (sesionCaja.es_sesion_vieja) {
    return (
      <div className="min-h-screen bg-gray-200 font-sans">
        <div className="flex justify-center py-20">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-10 max-w-md text-center">
            <div className="bg-red-100 rounded-full p-5 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
              <FaExclamationTriangle className="text-red-500 text-3xl" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-3">Caja de otro día</h2>
            <p className="text-gray-600 mb-6">
              Tienes una caja abierta de un día anterior ({sesionCaja.nombre_caja}). Debes cerrarla antes de continuar.
            </p>
            <a
              href="/CierreCaja"
              className="inline-block px-6 py-3 bg-red-600 text-white rounded-xl font-semibold hover:bg-red-700 transition-all duration-200 shadow-md"
            >
              Cerrar Caja
            </a>
          </div>
        </div>
      </div>
    );
  }

  const ticketActual = tm.ticketActual;
  const enModoEdicion = !!ticketActual.id_factura_editando;

  return (
    <div className="min-h-screen bg-gray-200 font-sans">
      <div className="flex justify-center py-2 md:py-10">
        <div className="bg-gray-100 bg-opacity-90 rounded-xl border p-3 md:p-8 w-full max-w-7xl mx-0 md:mx-10 pb-32 md:pb-32 lg:pb-8">
          
          {/* Header */}
          <div className="space-y-1 mb-6">
            <h1 className="font-cascadia font-bold text-2xl flex items-center gap-2">
              <FaFileInvoiceDollar className="text-color-secundario" />
              Factura Demo
            </h1>
          </div>

          {/* Banner modo edición */}
          {enModoEdicion && (
            <div className="mb-6 p-4 bg-amber-50 border border-amber-300 rounded-xl flex items-center gap-3">
              <FaEdit className="text-amber-600 text-xl flex-shrink-0" />
              <div>
                <p className="font-semibold text-amber-800">
                  Modo Edición — Factura #{ticketActual.id_factura_editando}
                </p>
                <p className="text-sm text-amber-700">
                  Los cambios en cantidades ajustarán el inventario automáticamente al guardar.
                </p>
              </div>
              <button
                onClick={() => {
                  if (window.confirm('¿Desea cancelar la edición? Los cambios no guardados se perderán.')) {
                    tm.limpiarTicketActivo();
                  }
                }}
                className="ml-auto px-3 py-1 bg-amber-200 text-amber-800 rounded-lg hover:bg-amber-300 transition text-sm font-medium"
              >
                Cancelar edición
              </button>
            </div>
          )}

          {/* Selector/Label de Sucursal */}
          <div className="mb-6">
            {!tm.sucursalSeleccionada ? (
              <div className="bg-white p-4 rounded-xl border border-gray-300">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sucursal <span className="text-red-500">*</span>
                </label>
                <select
                  value={tm.sucursalSeleccionada}
                  onChange={(e) => tm.setSucursalSeleccionada(e.target.value)}
                  className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-red-200 focus:border-red-500 focus:ring-2 focus:ring-red-100"
                >
                  {tm.sucursales.map((s) => (
                    <option key={s.id_sucursal} value={s.id_sucursal}>
                      {s.descripcion}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div className="flex justify-start items-center">
                <div className="bg-color-primario text-white px-4 py-2 rounded-lg flex items-center gap-3">
                  <span className="font-semibold">
                    Sucursal: {tm.sucursales.find(s => s.id_sucursal === parseInt(tm.sucursalSeleccionada))?.descripcion}
                  </span>
                  {!tm.sucursalDefecto && (
                    <button
                      onClick={() => {
                        if (tm.tickets.some(t => t.productos.length > 0)) {
                          if (!window.confirm('Hay tickets con productos. ¿Seguro que desea cambiar de sucursal?')) {
                            return;
                          }
                        }
                        tm.setSucursalSeleccionada('');
                      }}
                      className="text-xs bg-white text-color-primario px-2 py-1 rounded hover:bg-gray-100 transition"
                    >
                      Cambiar
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Sistema de Tabs */}
          <div className="mb-6">
            <div 
              className="flex items-center gap-2 overflow-x-auto pb-2"
              style={{ scrollbarWidth: 'thin', scrollbarColor: '#9CA3AF #E5E7EB' }}
            >
              {tm.tickets.map((ticket, index) => (
                <div
                  key={ticket.id}
                  className={`flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 transition-colors cursor-pointer flex-shrink-0 whitespace-nowrap ${
                    tm.ticketActivo === index
                      ? 'bg-color-secundario text-white border-color-secundario'
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => tm.setTicketActivo(index)}
                >
                  <span className="font-medium text-sm">
                    {ticket.id_factura_editando ? `✏️ Factura #${ticket.id_factura_editando}` : ticket.nombre}
                  </span>
                  {ticket.productos.length > 0 && (
                    <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${
                      tm.ticketActivo === index 
                        ? 'bg-white text-color-secundario' 
                        : 'bg-color-secundario text-white'
                    }`}>
                      {ticket.productos.length}
                    </span>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      tm.cerrarTicket(index);
                    }}
                    className="hover:text-red-500 transition-colors"
                  >
                    <FaTimes size={12} />
                  </button>
                </div>
              ))}
              
              {tm.tickets.length < 10 && (
                <button
                  onClick={tm.crearNuevoTicket}
                  className="flex items-center gap-1 px-3 py-2 bg-color-primario text-white rounded-t-lg hover:bg-opacity-90 transition-colors flex-shrink-0 text-sm"
                >
                  <FaPlus size={10} />
                  Nuevo
                </button>
              )}
            </div>
          </div>

          {/* Información del Ticket Activo */}
          <div className="mb-6 bg-white p-4 rounded-xl border border-gray-300">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">
              {enModoEdicion 
                ? `Editando Factura #${ticketActual.id_factura_editando}` 
                : `Información de ${ticketActual.nombre}`}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Cliente (opcional) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cliente <span className="text-gray-400">(opcional)</span>
                </label>
                <InputClientes
                  valor={ticketActual.clienteSeleccionado}
                  onClick={tm.handleClickBuscarCliente}
                  nombreCliente={ticketActual.nombreCliente}
                  placeholder="Sin cliente seleccionado"
                  editable={true}
                />
                <button
                  onClick={() => tm.setMostrarModalNuevoCliente(true)}
                  className="text-sm text-color-primario hover:text-color-secundario mt-1 underline"
                >
                  ¿Es un nuevo cliente? Crear aquí
                </button>
              </div>

              {/* Sin comprobante - informativo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tipo Comprobante
                </label>
                <div className="w-full p-2 rounded-xl text-gray-500 border-2 border-gray-200 bg-gray-50">
                  Sin comprobante fiscal
                </div>
                <p className="text-xs text-gray-400 mt-1">
                  Este documento no genera NCF
                </p>
              </div>
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Comentario de esta factura <span className="text-gray-400">(opcional)</span>
              </label>
              <textarea
                value={ticketActual.comentario || ''}
                onChange={(e) => actualizarComentarioTicket(e.target.value)}
                onBlur={normalizarComentarioTicket}
                className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                placeholder="Este comentario se imprime en esta factura y no reemplaza la nota al pie global."
                rows={2}
              />
            </div>

            {/* Campos adicionales */}
            <div className="mt-4 pt-3 border-t border-gray-200">
              <button
                onClick={() => tm.setMostrarCamposAdicionales(!tm.mostrarCamposAdicionales)}
                className="flex items-center gap-2 text-sm text-gray-600 hover:text-color-primario transition-colors"
              >
                {tm.mostrarCamposAdicionales ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
                <span>Campos adicionales</span>
              </button>

              {tm.mostrarCamposAdicionales && (
                <div className="mt-4 grid grid-cols-1 gap-4 animate-fadeIn">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Fecha y hora de creación
                    </label>
                    <input
                      type="datetime-local"
                      value={ticketActual.fecha_hora_creacion || ''}
                      max={obtenerFechaHoraActual()}
                      onChange={(e) => {
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].fecha_hora_creacion = e.target.value;
                        tm.setTickets(nuevosTickets);
                      }}
                      className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    />
                    {ticketActual.fecha_hora_creacion && !noEsFechaHoraFutura(ticketActual.fecha_hora_creacion) && (
                      <p className="text-xs text-red-500 mt-1">
                        La fecha/hora no puede ser futura.
                      </p>
                    )}
                  </div>
                  {!enModoEdicion && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Fecha de emisión
                      </label>
                      <input
                        type="date"
                        value={ticketActual.fecha_emision || ''}
                        min={calcularFechaMinimaEmisionISO()}
                        max={obtenerFechaActual()}
                        onChange={(e) => {
                          const nuevosTickets = [...tm.tickets];
                          nuevosTickets[tm.ticketActivo].fecha_emision = e.target.value;
                          tm.setTickets(nuevosTickets);
                        }}
                        className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                      />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Centro de costo
                    </label>
                    <select
                      value={ticketActual.id_centro_costo || ''}
                      onChange={(e) => {
                        const nuevosTickets = [...tm.tickets];
                        nuevosTickets[tm.ticketActivo].id_centro_costo = e.target.value ? parseInt(e.target.value) : null;
                        tm.setTickets(nuevosTickets);
                      }}
                      className="w-full p-2 rounded-xl text-gray-700 border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
                    >
                      <option value="">Sin centro de costo</option>
                      {tm.centrosCosto.map((centro) => (
                        <option key={centro.id_centro_costo} value={centro.id_centro_costo}>
                          {centro.descripcion}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Layout principal */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            
            <div className="lg:col-span-2 space-y-6">
              
              {/* Búsqueda de producto */}
              <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-300">
                <label className="block text-lg font-semibold text-gray-800 mb-3">
                  Buscar Producto
                </label>
                <p className="text-sm text-gray-500 mb-3">
                  Escanea código de barras o busca por nombre/referencia
                </p>
                <InputProductoFacturacion
                  ref={tm.inputProductoRef}
                  onProductoSeleccionado={tm.handleProductoSeleccionado}
                  onProductoRapido={tm.agregarProductoRapido}
                  onClickConProducto={tm.handleClickInputConProducto}
                  id_sucursal={tm.sucursalSeleccionada}
                  id_lista_precio={ticketActual.id_lista_precio}
                  modoFacturacion="venta"
                  editable={!!tm.sucursalSeleccionada}
                  placeholder="Referencia, código de barras o ID"
                />
                <div className="flex items-center justify-between mt-1">
                  <button
                    onClick={() => setMostrarModalNuevoProducto(true)}
                    className="text-sm text-color-primario hover:text-color-secundario underline"
                  >
                    ¿Producto no existe? Crear aquí
                  </button>
                  {balanzaConectada && (
                    <span className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${sinDatos ? 'bg-red-100 text-red-700' : balanzaEstable ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${sinDatos ? 'bg-red-500 animate-pulse' : balanzaEstable ? 'bg-green-500' : 'bg-amber-500 animate-pulse'}`} />
                      {sinDatos ? 'Sin datos' : `${pesoActual.toFixed(3)} ${unidadPeso}`}
                    </span>
                  )}
                  {scannerHidHabilitado && scannerConectado && (
                    <span className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-blue-100 text-blue-700">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      Scanner HID
                    </span>
                  )}
                </div>
              </div>

              {/* Lista de productos */}
              <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-300">
                <h2 className="text-lg font-semibold mb-4">
                  {enModoEdicion 
                    ? `Productos — Factura #${ticketActual.id_factura_editando}`
                    : `Productos en ${ticketActual.nombre}`}
                </h2>
                
                {ticketActual.productos.length === 0 ? (
                  <div className="text-center py-12 text-gray-400">
                    <FaFileInvoiceDollar size={48} className="mx-auto mb-4 opacity-30" />
                    <p className="text-lg">Ticket vacío</p>
                    <p className="text-sm">Agrega productos para comenzar</p>
                  </div>
                ) : (
                  <>
                  <div className="md:hidden space-y-2">
                    {ticketActual.productos.map((producto, index) => {
                      const cantOriginal = enModoEdicion && ticketActual.productosOriginales
                        ? ticketActual.productosOriginales.find(p => p.id_producto === producto.id_producto)?.cantidad
                        : null;
                      const cambio = cantOriginal !== null && cantOriginal !== undefined && cantOriginal !== producto.cantidad;
                      return (
                        <div
                          key={index}
                          className={`border border-gray-200 rounded-xl p-3 active:bg-gray-50 ${cambio ? 'bg-amber-50' : ''}`}
                          onClick={() => tm.editarCantidadProducto(index)}
                        >
                          <div className="flex justify-between items-start gap-2">
                            <div className="min-w-0">
                              <div className="font-medium leading-tight">{producto.descripcion}</div>
                              <div className="text-xs text-gray-500">
                                {producto.indicador_facturacion === 4 ? 'Exento' : `ITBIS ${producto.itbis_tasa}%`}
                              </div>
                              {enModoEdicion && (
                                <div className="text-xs text-amber-600 font-medium mt-1">
                                  Antes: {cantOriginal ?? 'NUEVO'}
                                </div>
                              )}
                            </div>
                            <button
                              onClick={(e) => { e.stopPropagation(); tm.eliminarProducto(index); }}
                              className="text-red-500 p-2 -m-2"
                              title="Eliminar"
                            >
                              <FaTrash size={18} />
                            </button>
                          </div>
                          <div className="flex justify-between items-end mt-2 text-sm">
                            <span className="text-gray-600">
                              {producto.cantidad} × {formatoMoneda(producto.precio)}
                              {producto.precioEditado && (
                                <span className="ml-1 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">Precio mod.</span>
                              )}
                            </span>
                            <span className="font-semibold text-base text-color-secundario">
                              {formatoMoneda(producto.precio * producto.cantidad + tm.calcularImpuestoPorProducto(producto))}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="min-w-full">
                      <thead className="bg-color-primario text-white">
                        <tr>
                          <th className="px-4 py-3 text-left">Producto</th>
                          <th className="px-4 py-3 text-right">Precio</th>
                          <th className="px-4 py-3 text-center">Cant.</th>
                          {enModoEdicion && <th className="px-4 py-3 text-center">Ant.</th>}
                          <th className="px-4 py-3 text-right">Subtotal</th>
                          <th className="px-4 py-3 text-center">Acciones</th>
                        </tr>
                      </thead>
                      <tbody>
                        {ticketActual.productos.map((producto, index) => {
                          // En modo edición, mostrar la cantidad original
                          const cantOriginal = enModoEdicion && ticketActual.productosOriginales
                            ? ticketActual.productosOriginales.find(p => p.id_producto === producto.id_producto)?.cantidad
                            : null;
                          const cambio = cantOriginal !== null && cantOriginal !== undefined && cantOriginal !== producto.cantidad;

                          return (
                            <tr
                              key={index}
                              className={`border-b hover:bg-gray-50 cursor-pointer ${cambio ? 'bg-amber-50' : ''}`}
                              onClick={() => tm.editarCantidadProducto(index)}
                            >
                              <td className="px-4 py-3">
                                <div className="font-medium">{producto.descripcion}</div>
                                <div className="text-xs text-gray-500">
                                  {producto.indicador_facturacion === 4 ? 'Exento' : `ITBIS ${producto.itbis_tasa}%`}
                                </div>
                                {cambio && (
                                  <div className="text-xs text-amber-600 font-medium mt-1">
                                    ⚠ Cantidad modificada
                                  </div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-right">
                                <span>{formatoMoneda(producto.precio)}</span>
                                {producto.precioEditado && (
                                  <span className="ml-1 text-xs bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded-full">Precio mod.</span>
                                )}
                              </td>
                              <td className="px-4 py-3 text-center font-semibold">
                                {producto.cantidad}
                              </td>
                              {enModoEdicion && (
                                <td className="px-4 py-3 text-center text-gray-400">
                                  {cantOriginal ?? <span className="text-green-600 text-xs font-medium">NUEVO</span>}
                                </td>
                              )}
                              <td className="px-4 py-3 text-right font-semibold text-color-secundario">
                                {formatoMoneda(producto.precio * producto.cantidad + tm.calcularImpuestoPorProducto(producto))}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex justify-center gap-2">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      tm.editarCantidadProducto(index);
                                    }}
                                    className="text-color-primario hover:text-color-secundario transition"
                                    title="Editar cantidad"
                                  >
                                    <FaEdit size={16} />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      tm.eliminarProducto(index);
                                    }}
                                    className="text-red-500 hover:text-red-700 transition"
                                    title="Eliminar"
                                  >
                                    <FaTrash size={16} />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  </>
                )}
              </div>
            </div>

            {/* Panel de totales */}
            <div className="lg:col-span-1">
              <div className="bg-white p-4 md:p-6 rounded-xl border border-gray-300 lg:sticky lg:top-4">
                <h2 className="text-lg font-semibold mb-4 text-gray-800">
                  {enModoEdicion ? 'Resumen de Edición' : 'Resumen de Venta'}
                </h2>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between text-gray-600">
                    <span>Subtotal:</span>
                    <span className="font-medium">{formatoMoneda(ticketActual.totales.subtotal)}</span>
                  </div>
                  
                  <div className="flex justify-between text-gray-600">
                    <span>ITBIS:</span>
                    <span className="font-medium">{formatoMoneda(ticketActual.totales.itbis)}</span>
                  </div>
                  
                  <div className="border-t pt-3 mt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-bold text-gray-800">TOTAL:</span>
                      <span className="text-2xl font-bold text-green-600">
                        {formatoMoneda(ticketActual.totales.total)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Botón Cobrar / Guardar */}
                <button
                  onClick={cobrarTicket}
                  disabled={ticketActual.productos.length === 0 || !tm.sucursalSeleccionada || tm.procesandoCobro}
                  className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                    ticketActual.productos.length === 0 || !tm.sucursalSeleccionada || tm.procesandoCobro
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : enModoEdicion
                        ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-xl'
                        : 'bg-green-600 hover:bg-green-700 text-white shadow-lg hover:shadow-xl'
                  }`}
                >
                  {tm.procesandoCobro 
                    ? 'PROCESANDO...' 
                    : enModoEdicion 
                      ? 'GUARDAR CAMBIOS' 
                      : 'COBRAR'}
                </button>
                
                {ticketActual.productos.length === 0 && (
                  <p className="text-xs text-gray-400 text-center mt-2">
                    Agrega productos para {enModoEdicion ? 'guardar' : 'cobrar'}
                  </p>
                )}
                {!tm.sucursalSeleccionada && ticketActual.productos.length > 0 && (
                  <p className="text-xs text-red-500 text-center mt-2">
                    Selecciona una sucursal
                  </p>
                )}

                {/* Botón editar factura existente */}
                {!enModoEdicion && (
                  <button
                    onClick={handleBotonEditar}
                    className="w-full mt-3 py-2 rounded-xl font-medium text-sm transition-all border-2 border-amber-400 text-amber-700 hover:bg-amber-50"
                  >
                    <FaSearch className="inline mr-2" />
                    Editar factura existente
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Barra de cobro fija (móvil / tablet) */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 md:left-[16.5rem] z-40 pb-[max(0.5rem,env(safe-area-inset-bottom))] bg-white border-t border-gray-300 shadow-[0_-2px_8px_rgba(0,0,0,0.1)] px-3 py-2 flex items-center gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-xs text-gray-500">
            {ticketActual.productos.length} {ticketActual.productos.length === 1 ? 'artículo' : 'artículos'} · ITBIS {formatoMoneda(ticketActual.totales.itbis)}
          </div>
          <div className="text-xl font-bold text-green-600 truncate">{formatoMoneda(ticketActual.totales.total)}</div>
        </div>
        <button
          onClick={cobrarTicket}
          disabled={ticketActual.productos.length === 0 || !tm.sucursalSeleccionada || tm.procesandoCobro}
          className={`px-5 py-3 rounded-xl font-bold text-base ${
            ticketActual.productos.length === 0 || !tm.sucursalSeleccionada || tm.procesandoCobro
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : enModoEdicion
                ? 'bg-amber-500 active:bg-amber-600 text-white shadow-lg'
                : 'bg-green-600 active:bg-green-700 text-white shadow-lg'
          }`}
        >
          {tm.procesandoCobro ? '...' : enModoEdicion ? 'GUARDAR' : 'COBRAR'}
        </button>
      </div>

      {/* Modal para especificar cantidad */}
      <PlantillaModal
        mostrar={tm.mostrarModalCantidad}
        onClose={() => {
          tm.setMostrarModalCantidad(false);
          tm.setProductoTemporal(null);
          tm.setCantidadTemporal('1');
          tm.setPrecioTemporal('');
          tm.setModoModal('agregar');
          tm.setIndexProductoEditar(null);
          tm.setModoPrecioTemporal('unitario');
          desbloquearPeso();
          setTimeout(() => {
            tm.inputProductoRef.current?.limpiar?.();
          }, 100);
        }}
        titulo={tm.modoModal === 'editar' ? 'Editar Producto' : 'Agregar Producto'}
      >
        {tm.productoTemporal && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600">Producto:</p>
              <p className="font-semibold text-lg">{tm.productoTemporal.descripcion}</p>
              {tm.productoTemporal.descripcion_unidad_medida && (
                <p className="text-sm text-gray-500 italic mt-1">
                  Unidad: {tm.productoTemporal.descripcion_unidad_medida}
                </p>
              )}
            </div>
            
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">Precio unitario:</label>
                {tienePermiso('venta_editar_precio') && (
                  <div className="flex rounded-lg border border-gray-300 overflow-hidden text-xs">
                    <button
                      type="button"
                      onClick={() => tm.setModoPrecioTemporal('unitario')}
                      className={`px-2 py-1 transition-colors ${tm.modoPrecioTemporal === 'unitario' ? 'bg-color-primario text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Sin impuestos
                    </button>
                    <button
                      type="button"
                      onClick={() => tm.setModoPrecioTemporal('total')}
                      className={`px-2 py-1 transition-colors border-l border-gray-300 ${tm.modoPrecioTemporal === 'total' ? 'bg-color-primario text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                    >
                      Con impuestos
                    </button>
                  </div>
                )}
              </div>

              {tienePermiso('venta_editar_precio') ? (
                tm.modoPrecioTemporal === 'total' ? (
                  <>
                    <input
                      type="number"
                      step="0.01"
                      value={tm.totalTemporalConImpuesto}
                      onChange={(e) => tm.actualizarTotalTemporal(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          tm.inputCantidadRef.current?.focus();
                        }
                      }}
                      placeholder="Total con ITBIS incluido"
                      className="w-full p-2 text-xl text-center font-semibold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                      tabIndex={1}
                    />
                    <p className="text-xs text-gray-500 mt-1 text-center">
                      Precio unitario (base): {formatoMoneda(parseFloat(tm.precioTemporal) || 0)}
                    </p>
                  </>
                ) : (
                  <input
                    type="number"
                    step="0.01"
                    value={tm.precioTemporal}
                    onChange={(e) => tm.setPrecioTemporal(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        tm.inputCantidadRef.current?.focus();
                      }
                    }}
                    className="w-full p-2 text-xl text-center font-semibold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                    tabIndex={1}
                  />
                )
              ) : (
                <p className="font-semibold text-xl text-color-secundario">
                  {formatoMoneda(tm.productoTemporal.precio || 0)}
                </p>
              )}

              <p className="text-xs text-gray-400 mt-1">
                Tasa ITBIS: {tm.obtenerTasaItbisEfectiva(tm.productoTemporal)}% (no editable)
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Cantidad{tm.productoTemporal.descripcion_unidad_medida && ` (${tm.productoTemporal.descripcion_unidad_medida})`}:
              </label>
              <input
                ref={tm.inputCantidadRef}
                type="number"
                value={tm.cantidadTemporal}
                onChange={(e) => {
                  const valor = e.target.value;
                  if (valor === '' || /^\d*\.?\d*$/.test(valor)) {
                    tm.setCantidadTemporal(valor);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    tm.agregarProductoAlTicket();
                  }
                }}
                min="0.01"
                step="0.01"
                className="w-full p-3 text-2xl text-center font-bold border-2 outline-none transition-all border-gray-300 focus:border-blue-500 focus:ring-2 focus:ring-blue-100 rounded-xl"
                autoFocus
                tabIndex={2}
              />
              {balanzaConectada && tm.productoTemporal?.es_pesable && (
                <div className="flex items-center gap-2 mt-2">
                  {pesoBloqueado ? (
                    <>
                      <span className="text-sm font-bold text-green-700">
                        {pesoBloqueado.peso.toFixed(3)} {pesoBloqueado.unidad} (capturado)
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          desbloquearPeso();
                          tm.setCantidadTemporal('');
                        }}
                        className="text-xs text-gray-500 underline hover:text-gray-700"
                      >
                        Desbloquear
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={`text-sm font-medium ${balanzaEstable ? 'text-green-700' : 'text-amber-600'}`}>
                        {pesoActual.toFixed(3)} {unidadPeso}
                      </span>
                      <button
                        type="button"
                        onClick={() => {
                          bloquearPeso();
                          if (pesoActual > 0) tm.setCantidadTemporal(pesoActual.toFixed(3));
                        }}
                        disabled={!balanzaEstable || pesoActual <= 0}
                        className="px-3 py-1 text-xs font-semibold bg-color-primario text-white rounded-lg hover:bg-color-secundario disabled:opacity-50 transition-colors"
                      >
                        Capturar peso
                      </button>
                      {!balanzaEstable && (
                        <span className="text-xs text-amber-500">Inestable</span>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>

            <div className="bg-gray-50 p-3 rounded-lg">
              <div className="flex justify-between items-center text-xs text-gray-500">
                <span>Antes de imp:</span>
                <span>
                  {formatoMoneda((parseFloat(tm.precioTemporal) || 0) * (parseFloat(tm.cantidadTemporal) || 0))}
                </span>
              </div>
              <div className="flex justify-between items-center mt-1">
                <span className="text-gray-600">Con imp:</span>
                <span className="text-2xl font-bold text-color-primario">
                  {formatoMoneda(
                    (parseFloat(tm.precioTemporal) || 0) * (parseFloat(tm.cantidadTemporal) || 0) *
                    (1 + tm.obtenerTasaItbisEfectiva(tm.productoTemporal) / 100)
                  )}
                </span>
              </div>
            </div>

            <button
              onClick={tm.agregarProductoAlTicket}
              className="w-full bg-color-secundario text-white py-3 rounded-xl font-semibold hover:bg-opacity-90 transition-all"
            >
              {tm.modoModal === 'editar' ? 'Actualizar' : 'Agregar al Ticket'}
            </button>
          </div>
        )}
      </PlantillaModal>

      {/* Modal para crear nuevo cliente */}
      <ModalCrearClienteRapido
        mostrar={tm.mostrarModalNuevoCliente}
        onClose={() => tm.setMostrarModalNuevoCliente(false)}
        onClienteCreado={tm.handleClienteCreado}
        tiposComprobantesFiscales={tm.tiposComprobantesFiscales}
      />

      {/* Modal para crear nuevo producto */}
      <ModalCrearProductoRapido
        mostrar={mostrarModalNuevoProducto}
        onClose={() => setMostrarModalNuevoProducto(false)}
        onProductoCreado={(productoCreado) => {
          setMostrarModalNuevoProducto(false);
          tm.handleProductoSeleccionado(productoCreado);
        }}
      />

      {/* Modal de Pago */}
      <ModalPagoPOS
        mostrar={tm.mostrarModalPago}
        onClose={() => tm.setMostrarModalPago(false)}
        ticket={ticketActual}
        onConfirmarPago={procesarPago}
        formatoMoneda={formatoMoneda}
        pasarelaDefecto={tm.pasarelaDefecto}
      />

      {/* Modal para seleccionar cliente */}
      <PlantillaModal 
        mostrar={tm.mostrarModalClientes} 
        onClose={() => tm.setMostrarModalClientes(false)}
        titulo="Seleccionar Cliente"
      >
        <TablaEntidad
          tipoEntidad={1}
          descripcion="Seleccione un cliente"
          setEntidadSeleccionada={tm.handleSeleccionarCliente}
        />
      </PlantillaModal>

      {/* Modal de Búsqueda de Facturas Editables */}
      <PlantillaModal
        mostrar={mostrarModalBuscarEditable}
        onClose={() => setMostrarModalBuscarEditable(false)}
        titulo="Buscar Factura para Editar"
      >
        <div className="space-y-4 min-w-[600px]">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl">
            <p className="text-sm text-amber-700">
              Solo se muestran facturas <strong>sin comprobante fiscal</strong> (editables).
            </p>
          </div>

          <div className="flex gap-2">
            <input
              type="text"
              className="flex-1 px-3 py-2 border border-gray-400 rounded-xl"
              placeholder="Buscar por ID, cliente..."
              value={filtroEditable}
              onChange={(e) => setFiltroEditable(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && buscarFacturasEditables(filtroEditable)}
            />
            <button
              onClick={() => buscarFacturasEditables(filtroEditable)}
              className="px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition"
            >
              Buscar
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded-xl">
            {cargandoEditables ? (
              <div className="text-center py-8"><SpinnerCargando /></div>
            ) : facturasEditables.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No se encontraron facturas editables (sin comprobante fiscal)
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-color-primario text-white sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Cliente</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Fecha</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase">Total</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {facturasEditables.map((factura) => (
                    <tr key={factura.id_factura} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-sm font-semibold">#{factura.id_factura}</td>
                      <td className="px-4 py-2 text-sm truncate max-w-[200px]">
                        {resolverNombreClienteFactura(factura)}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {new Date(factura.fecha_hora || factura.fecha_hora_creacion).toLocaleDateString('es-DO')}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        {formatoMoneda(factura.monto_total)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => cargarFacturaParaEditar(factura)}
                          className="px-3 py-1 bg-amber-500 text-white text-sm rounded-lg hover:bg-amber-600 transition"
                        >
                          Editar
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </PlantillaModal>

      {/* Modal de Búsqueda de Facturas (lupa del Dock) */}
      <PlantillaModal
        mostrar={tm.mostrarModalBusquedaFacturas}
        onClose={() => tm.setMostrarModalBusquedaFacturas(false)}
        titulo="Buscar Facturas"
      >
        <div className="space-y-4 min-w-[600px]">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">Buscar (Cliente o ID)</label>
              <input
                type="text"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl"
                placeholder="Nombre cliente, ID factura..."
                value={tm.filtrosBusqueda.busqueda}
                onChange={(e) => tm.setFiltrosBusqueda(prev => ({ ...prev, busqueda: e.target.value }))}
                onKeyDown={(e) => e.key === 'Enter' && tm.buscarFacturas(tm.filtrosBusqueda)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Desde</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl"
                value={tm.filtrosBusqueda.fecha_desde}
                onChange={(e) => tm.setFiltrosBusqueda(prev => ({ ...prev, fecha_desde: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Hasta</label>
              <input
                type="date"
                className="w-full px-3 py-2 border border-gray-400 rounded-xl"
                value={tm.filtrosBusqueda.fecha_hasta}
                onChange={(e) => tm.setFiltrosBusqueda(prev => ({ ...prev, fecha_hasta: e.target.value }))}
              />
            </div>
            <div className="flex items-end">
              <button
                onClick={() => tm.buscarFacturas(tm.filtrosBusqueda)}
                className="w-full px-4 py-2 bg-color-primario text-white rounded-xl hover:bg-opacity-90 transition"
              >
                Buscar
              </button>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={tm.limpiarFiltrosBusqueda}
              className="px-4 py-2 border border-gray-400 rounded-xl hover:bg-gray-100 transition"
            >
              Limpiar
            </button>
          </div>

          <div className="max-h-96 overflow-y-auto border rounded-xl">
            {tm.cargandoFacturas ? (
              <div className="text-center py-8"><SpinnerCargando /></div>
            ) : tm.facturasBuscadas.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No se encontraron facturas
              </div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-color-primario text-white sticky top-0">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">ID</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">NCF</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Cliente</th>
                    <th className="px-4 py-2 text-left text-xs font-medium uppercase">Fecha</th>
                    <th className="px-4 py-2 text-right text-xs font-medium uppercase">Total</th>
                    <th className="px-4 py-2 text-center text-xs font-medium uppercase">Acción</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {tm.facturasBuscadas.map((factura) => (
                    <tr key={factura.id_factura} className="hover:bg-gray-50">
                      <td className="px-4 py-2 font-mono text-sm font-semibold">#{factura.id_factura}</td>
                      <td className="px-4 py-2 font-mono text-sm">{factura.ncf || '—'}</td>
                      <td className="px-4 py-2 text-sm truncate max-w-[200px]">
                        {resolverNombreClienteFactura(factura)}
                      </td>
                      <td className="px-4 py-2 text-sm">
                        {new Date(factura.fecha_hora || factura.fecha_hora_creacion).toLocaleDateString('es-DO')}
                      </td>
                      <td className="px-4 py-2 text-right font-medium">
                        {formatoMoneda(factura.monto_total)}
                      </td>
                      <td className="px-4 py-2 text-center">
                        <button
                          onClick={() => tm.handleSeleccionarFacturaBuscada(factura)}
                          className="px-3 py-1 bg-color-secundario text-white text-sm rounded-lg hover:bg-opacity-90 transition"
                        >
                          Ver PDF
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </PlantillaModal>

      {/* Modal PDF Factura */}
      <ModalPDFBase64
        mostrar={tm.mostrarPDFFactura}
        onClose={() => {
          tm.setMostrarPDFFactura(false);
          tm.setPdfBase64(null);
          tm.setIdFacturaPDF(null);
        }}
        idFactura={tm.idFacturaPDF || tm.facturaCreada?.id_factura}
        ncf={tm.facturaCreada?.ncf}
        tituloDocumento="Factura Demo"
        tipoDocumento="factura"
      />

      {/* Modal Resumen de Inventario y Pagos (post-edición) */}
      <PlantillaModal
        mostrar={mostrarResumenInventario}
        onClose={() => {
          // Solo permitir cerrar si no hay pagos anulados o si el checkbox está marcado
          if (pagosAnulados === 0 || confirmacionPagosAnulados) {
            setMostrarResumenInventario(false);
            setResumenInventario(null);
            setPagosAnulados(0);
            setConfirmacionPagosAnulados(false);
          }
        }}
        titulo="Resumen de Cambios"
      >
        <div className="space-y-4 min-w-[500px]">
          {/* Fallback: no hubo cambios de inventario ni pagos anulados */}
          {(!resumenInventario || resumenInventario.length === 0) && pagosAnulados === 0 && (
            <div className="flex items-center gap-3 p-4 bg-green-50 rounded-xl border border-green-200">
              <FiCheckCircle className="text-green-600 text-xl flex-shrink-0" />
              <p className="text-green-800 font-medium">
                Factura actualizada correctamente. No se requirieron ajustes de inventario ni anulación de pagos.
              </p>
            </div>
          )}

          {/* Alerta de Pagos Anulados */}
          {pagosAnulados > 0 && (
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 rounded-r-xl">
              <div className="flex items-start gap-3">
                <FaExclamationTriangle className="text-amber-600 text-xl flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-bold text-amber-900 mb-1">Pagos Anulados Automáticamente</h4>
                  <p className="text-sm text-amber-800 leading-relaxed">
                    Se anularon <span className="font-bold">{pagosAnulados}</span> pago(s) existente(s) debido al cambio en el total de la factura.
                  </p>
                  <p className="text-sm text-amber-700 mt-2 font-medium">
                    ⚠️ Debe aplicar los pagos nuevamente desde <span className="font-bold">Cuentas por Cobrar</span>.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Tabla de Ajustes de Inventario */}
          {resumenInventario && resumenInventario.length > 0 && (
            <>
              <p className="text-sm text-gray-600">
                Los siguientes cambios de inventario fueron aplicados automáticamente:
              </p>
              <div className="border rounded-xl overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Producto</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Anterior</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Nueva</th>
                      <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 uppercase">Ajuste</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {resumenInventario.map((item, idx) => (
                      <tr key={idx}>
                        <td className="px-4 py-2 text-sm font-medium">{item.descripcion}</td>
                        <td className="px-4 py-2 text-center text-sm">{item.cantidad_anterior}</td>
                        <td className="px-4 py-2 text-center text-sm">{item.cantidad_nueva}</td>
                        <td className={`px-4 py-2 text-center text-sm font-bold ${
                          item.delta > 0 ? 'text-red-600' : 'text-green-600'
                        }`}>
                          {item.delta > 0 ? `-${item.delta}` : `+${Math.abs(item.delta)}`}
                          <span className="text-xs font-normal text-gray-400 ml-1">
                            ({item.accion})
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}

          {/* Checkbox de confirmación (obligatorio si hay pagos anulados) */}
          {pagosAnulados > 0 && (
            <div className="bg-gray-50 p-4 rounded-xl border-2 border-gray-200">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={confirmacionPagosAnulados}
                  onChange={(e) => setConfirmacionPagosAnulados(e.target.checked)}
                  className="mt-1 h-5 w-5 text-color-primario rounded focus:ring-2 focus:ring-color-primario"
                />
                <span className="text-sm text-gray-700 font-medium leading-relaxed">
                  Entiendo que los pagos fueron anulados y que debo aplicarlos nuevamente desde Cuentas por Cobrar.
                </span>
              </label>
            </div>
          )}

          <button
            onClick={async () => {
              setMostrarResumenInventario(false);
              setResumenInventario(null);
              setPagosAnulados(0);
              setConfirmacionPagosAnulados(false);
              toast.success('Factura actualizada correctamente');
              tm.limpiarTicketActivo();
              tm.setMostrarModalPago(false);

              // Imprimir via QZ Tray (ESC/POS) si está disponible, sino fallback a browser
              const pdfData = tm.facturaCreada?.pdf_base64 || null;
              await imprimirDocumento({
                idDocumento: tm.facturaCreada?.id_factura,
                tipoDocumento: 'factura',
                pdfBase64: pdfData
              });
            }}
            disabled={pagosAnulados > 0 && !confirmacionPagosAnulados}
            className={`w-full py-2 rounded-xl font-medium transition ${
              pagosAnulados > 0 && !confirmacionPagosAnulados
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-color-primario text-white hover:bg-opacity-90'
            }`}
          >
            Entendido
          </button>
        </div>
      </PlantillaModal>

      <ModalConfirmacion
        mostrar={tm.confirmacionCambioLista.mostrar}
        onConfirmar={tm.confirmarCambioListaCliente}
        onCancelar={tm.cancelarCambioListaCliente}
        titulo={tm.confirmacionCambioLista.titulo}
        mensaje={tm.confirmacionCambioLista.mensaje}
        textoConfirmar={tm.confirmacionCambioLista.textoConfirmar}
        textoCancelar={tm.confirmacionCambioLista.textoCancelar}
        tipo={tm.confirmacionCambioLista.tipo}
      />

      {/* Dock de acciones (oculto en móvil) */}
      <div className="hidden md:block">
      <DockVerticalDerecha
        handleBotonCrear={tm.crearNuevoTicket}
        handleBotonEditar={handleBotonEditar}
        handleBotonBuscar={tm.handleBotonBuscar}
      />
      </div>
    </div>
  );
}

